import {
  Inbox,
  CircleDot,
  Target,
  LayoutDashboard,
  DollarSign,
  History,
  Search,
  SquarePen,
  Network,
  Boxes,
  Repeat,
  GitBranch,
  Package,
  Settings,
  LineChart,
  BookOpen,
  Bell,
  TrendingUp,
  Landmark,
  ShoppingCart,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { NavLink } from "@/lib/router";
import { SidebarSection } from "./SidebarSection";
import { SidebarNavItem } from "./SidebarNavItem";
import { SidebarProjects } from "./SidebarProjects";
import { SidebarAgents } from "./SidebarAgents";
import { useDialogActions } from "../context/DialogContext";
import { useCompany } from "../context/CompanyContext";
import { useSidebar } from "../context/SidebarContext";
import { cn } from "../lib/utils";
import { heartbeatsApi } from "../api/heartbeats";
import { instanceSettingsApi } from "../api/instanceSettings";
import { queryKeys } from "../lib/queryKeys";
import { useInboxBadge } from "../hooks/useInboxBadge";
import { Button } from "@/components/ui/button";
import { PluginSlotOutlet } from "@/plugins/slots";
import { PluginLauncherOutlet } from "@/plugins/launchers";
import { SidebarCompanyMenu } from "./SidebarCompanyMenu";

export function Sidebar() {
  const { openNewIssue } = useDialogActions();
  const { selectedCompanyId, selectedCompany } = useCompany();
  const inboxBadge = useInboxBadge(selectedCompanyId);
  const location = useLocation();
  const isTradeRoute = location.pathname.includes("/trade");
  const { collapsed } = useSidebar();
  const { data: experimentalSettings } = useQuery({
    queryKey: queryKeys.instance.experimentalSettings,
    queryFn: () => instanceSettingsApi.getExperimental(),
  });
  const { data: liveRuns } = useQuery({
    queryKey: queryKeys.liveRuns(selectedCompanyId!),
    queryFn: () => heartbeatsApi.liveRunsForCompany(selectedCompanyId!),
    enabled: !!selectedCompanyId,
    refetchInterval: 10_000,
  });
  const liveRunCount = liveRuns?.length ?? 0;
  const showWorkspacesLink = experimentalSettings?.enableIsolatedWorkspaces === true;

  const pluginContext = {
    companyId: selectedCompanyId,
    companyPrefix: selectedCompany?.issuePrefix ?? null,
  };

  if (collapsed) {
    return (
      <aside className="w-full h-full min-h-0 border-r border-border bg-background flex flex-col items-center">
        <div className="flex items-center justify-center h-12 shrink-0 w-full">
          {isTradeRoute ? (
            <span className="text-xs font-bold text-foreground truncate px-1" title={selectedCompany?.name}>
              {selectedCompany?.issuePrefix ?? selectedCompany?.name?.slice(0, 3).toUpperCase() ?? "OTC"}
            </span>
          ) : (
            <SidebarCompanyMenu />
          )}
        </div>
        <nav className="flex-1 min-h-0 overflow-y-auto scrollbar-auto-hide flex flex-col gap-3 py-2 items-center w-full">
          {isTradeRoute ? (
            <>
              <CompactNavItem to="/factories" icon={Boxes} label="Factories" />
              <CompactNavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" />
              <CompactNavItem to="/trade" icon={TrendingUp} label="Trade" end />
              <div className="w-6 h-px bg-border my-1" />
              <CompactNavItem to="/trade?tab=markets" icon={LineChart} label="Markets" />
              <CompactNavItem to="/trade?tab=orders" icon={BookOpen} label="Orders" />
              <CompactNavItem to="/trade?tab=alerts" icon={Bell} label="Alerts" />
            </>
          ) : (
            <>
              <CompactNavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" />
              <CompactNavItem to="/trade" icon={TrendingUp} label="Trade" />
              <CompactNavItem to="/inbox" icon={Inbox} label="Inbox" />
              <div className="w-6 h-px bg-border my-1" />
              <CompactNavItem to="/issues" icon={CircleDot} label="Issues" />
              <CompactNavItem to="/routines" icon={Repeat} label="Routines" />
              <CompactNavItem to="/goals" icon={Target} label="Goals" />
              <div className="w-6 h-px bg-border my-1" />
              <CompactNavItem to="/org" icon={Network} label="Org" />
              <CompactNavItem to="/skills" icon={Boxes} label="Skills" />
              <CompactNavItem to="/commerce" icon={ShoppingCart} label="Commerce" />
              <CompactNavItem to="/tax" icon={Landmark} label="Tax & Compliance" />
              <CompactNavItem to="/costs" icon={DollarSign} label="Costs" />
              <CompactNavItem to="/activity" icon={History} label="Activity" />
              <CompactNavItem to="/company/settings" icon={Settings} label="Settings" />
            </>
          )}
        </nav>
      </aside>
    );
  }

  return (
    <aside className="w-full h-full min-h-0 border-r border-border bg-background flex flex-col">
      {/* Top bar: Company name (bold) + Search — aligned with top sections (no visible border) */}
      <div className="flex items-center gap-1 px-3 h-12 shrink-0">
        {isTradeRoute ? (
          <div className="flex items-center gap-2 h-9 px-2 flex-1 min-w-0">
            <span className="h-2 w-2 rounded-full shrink-0 bg-green-400" />
            <span className="text-sm font-bold truncate text-foreground">
              {selectedCompany?.name ?? "OTC"}
            </span>
          </div>
        ) : (
          <SidebarCompanyMenu />
        )}
        <Button
          asChild
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground shrink-0"
          aria-label="Open search"
          title="Open search"
        >
          <NavLink to="/search">
            <Search className="h-4 w-4" />
          </NavLink>
        </Button>
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto scrollbar-auto-hide flex flex-col gap-4 pointer-coarse:gap-3 px-3 py-2">
        {isTradeRoute ? (
          <>
            <div className="flex flex-col gap-0.5">
              <SidebarNavItem to="/dashboard" label="Dashboard" icon={LayoutDashboard} />
              <SidebarNavItem to="/trade" label="Trade" icon={TrendingUp} end />
            </div>
            <SidebarSection label="Trade">
              <SidebarNavItem to="/trade?tab=markets" label="Markets" icon={LineChart} />
              <SidebarNavItem to="/trade?tab=orders" label="Orders" icon={BookOpen} />
              <SidebarNavItem to="/trade?tab=alerts" label="Alerts" icon={Bell} />
            </SidebarSection>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-0.5">
              {/* New Issue button aligned with nav items */}
              <button
                onClick={() => openNewIssue()}
                data-slot="icon-button"
                className="flex items-center gap-2.5 px-3 py-2 pointer-coarse:py-1.5 text-[13px] font-medium text-muted-foreground hover:bg-accent/50 hover:text-foreground transition-colors"
              >
                <SquarePen className="h-4 w-4 shrink-0" />
                <span className="truncate">New Issue</span>
              </button>
              <SidebarNavItem to="/dashboard" label="Dashboard" icon={LayoutDashboard} liveCount={liveRunCount} />
              <SidebarNavItem to="/trade" label="Trade" icon={TrendingUp} />
              <SidebarNavItem
                to="/inbox"
                label="Inbox"
                icon={Inbox}
                badge={inboxBadge.inbox}
                badgeTone={inboxBadge.failedRuns > 0 ? "danger" : "default"}
                alert={inboxBadge.failedRuns > 0}
              />
            </div>

            <SidebarSection label="Work">
              <SidebarNavItem to="/factories" label="Factories" icon={Boxes} />
              <SidebarNavItem to="/issues" label="Issues" icon={CircleDot} />
              <SidebarNavItem to="/routines" label="Routines" icon={Repeat} />
              <SidebarNavItem to="/goals" label="Goals" icon={Target} />
              <SidebarNavItem to="/artifacts" label="Artifacts" icon={Package} />
              {showWorkspacesLink ? (
                <SidebarNavItem to="/workspaces" label="Workspaces" icon={GitBranch} />
              ) : null}
              <PluginSlotOutlet
                slotTypes={["sidebar"]}
                context={pluginContext}
                className="flex flex-col gap-0.5"
                itemClassName="text-[13px] font-medium"
                missingBehavior="placeholder"
              />
              <PluginLauncherOutlet
                placementZones={["sidebar"]}
                context={pluginContext}
                className="flex flex-col gap-0.5"
                itemClassName="text-[13px] font-medium"
              />
            </SidebarSection>

            <SidebarProjects />

            <SidebarAgents />

            <SidebarSection label="Company">
              <SidebarNavItem to="/org" label="Org" icon={Network} />
              <SidebarNavItem to="/skills" label="Skills" icon={Boxes} />
              <SidebarNavItem to="/commerce" label="Commerce" icon={ShoppingCart} />
              <SidebarNavItem to="/costs" label="Costs" icon={DollarSign} />
              <SidebarNavItem to="/tax" label="Tax & Compliance" icon={Landmark} />
              <SidebarNavItem to="/activity" label="Activity" icon={History} />
              <SidebarNavItem to="/company/settings" label="Settings" icon={Settings} />
            </SidebarSection>

            <PluginSlotOutlet
              slotTypes={["sidebarPanel"]}
              context={pluginContext}
              className="flex flex-col gap-3"
              itemClassName="rounded-lg border border-border p-3"
              missingBehavior="placeholder"
            />
          </>
        )}
      </nav>
    </aside>
  );
}

function CompactNavItem({ to, icon: Icon, label, end }: { to: string; icon: typeof LayoutDashboard; label: string; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "flex items-center justify-center w-9 h-9 rounded-md transition-colors",
          isActive
            ? "bg-accent text-foreground"
            : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
        )
      }
      title={label}
    >
      <Icon className="h-4 w-4" />
    </NavLink>
  );
}
